import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Box,
  Button,
  Center,
  CircularProgress,
  Heading,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, SettingsIcon, WarningIcon } from "@chakra-ui/icons";
import { Slide } from "yet-another-react-lightbox";

import { getFirmProHistory, getPhotoLinks } from "../utils/api";
import { DetallesFP, Historial } from "../utils/interfaces";
import {
  getDarkTextIssueColor,
  getLightTextIssueColor,
} from "../utils/errorColors";
import FPPlot from "./FPPlot";
import IssueLog from "./IssueLog";
import FPConfigForm from "./FPConfigForm";
import parseTime from "../utils/parseTime";
import { MdPhoto } from "react-icons/md";
import { baseURL } from "../utils/api";
import CustomLightbox from "./CustomLightbox";

const SECOND = 1000;

/**
 * Página de detalles de un solo equipo FirmPro.
 *
 * @returns {JSX.Element}
 */
export default function Dashboard(): JSX.Element {
  const { isOpen: isFPSettingsModalOpen, onOpen: onFPSettingsModalOpen, onClose: onFPSettingsModalClose } = useDisclosure();
  const navigate = useNavigate();
  const nfp = Number(useParams().id); // Se extrae el NFP de la URL.
  const [detalles, setDetalles] = useState<DetallesFP | null>(null);
  const [historial, setHistorial] = useState<Historial | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [photoSlides, setPhotoSlides] = useState<Slide[]>([]);

  const getDetalles = useCallback(() => {
    setErrorMessage(null);
    getFirmProHistory(nfp)
      .then((res) => {
        setDetalles(res.data);
      })
      .catch((err) => {
        setDetalles(null);
        
        let innerErrorMessage = "";
        if (err.response) {
          // Si el backend respondió con un error (código no 2xx):
          innerErrorMessage = err.response.data as string;
        } else if (err.request) {
          // Si hubo petición, pero no respuesta del servidor:
          innerErrorMessage = "Error de conexión con el servidor."
        } else {
          // Si algo más salió mal:
          innerErrorMessage = err.message;
        }
        setErrorMessage(innerErrorMessage);
      });
  }, [nfp]);

  const onOpenGallery = () => {
    const editSlide = (slide: Slide): Slide => ({ ...slide, src: baseURL + slide.src });
    getPhotoLinks(nfp)
      .then(res => {
        setPhotoSlides(res.data.map(editSlide));
        console.log(res.data);
        setIsGalleryOpen(true);
      })
      .catch(err => console.log("Error al obtener links de fotos:", err));
  };

  useEffect(() => {
    getDetalles();
    const interval = setInterval(getDetalles, 30 * SECOND);
    return () => clearInterval(interval);
  }, [getDetalles]);

  if (errorMessage !== null) {
    return (
      <Box px={6} py={{ base: 3, lg: 5 }}>
        <HStack justifyContent="space-between">
          <Button
            onClick={() => navigate("/")}
            borderRadius="lg"
            aria-label="Regresar"
          >
            <ArrowBackIcon />
            &ensp;Regresar
          </Button>
        </HStack>
        <Center height={window.innerHeight - 80}>
          <VStack spacing="8">
            <WarningIcon boxSize="150" color="red.500" />
            <Text fontSize="2xl" align="center">
              {errorMessage}<br />
              Espera 30 segundos para intentar cargar datos de nuevo.
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (detalles === null) {
    return (
      <Box px={6} py={{ base: 3, lg: 5 }}>
        <HStack justifyContent="space-between">
          <Button
            onClick={() => navigate("/")}
            borderRadius="lg"
            aria-label="Regresar"
          >
            <ArrowBackIcon />
            &ensp;Regresar
          </Button>
        </HStack>
        <Center height={window.innerHeight - 80}>
          <VStack spacing="8">
            <CircularProgress isIndeterminate size="150" color="teal" />
            <Text fontSize="2xl" align="center">
              Cargando...
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  const { ip, eventos, historial_conexion, lotes, mediciones_regulares, fallas, fallas_por_dia } =
    detalles;

  /**
   * Revisa el historial de todos los nombres que tuvo este equipo y obtiene
   * el más reciente, es decir, el nombre que tiene ahora.
   *
   * @returns {string} El nombre actual del equipo FirmPro.
   */
  const getName = (): string => {
    for (let historial of eventos) {
      const { tipo_evento, mapa_valores } = historial;
      if (tipo_evento.nombre_variable === "nombre_fp") {
        const valores = mapa_valores.main;
        if (valores.length === 0) {
          return "[Sin nombre]";
        }
        return valores[0];
      }
    }
    return "[Sin nombre]";
  };

  /**
   * Toma un historial de datos y, si tiene una descripción, lo convierte en
   * un párrafo <p> donde:
   * - Se puede pasar el cursor por encima para mostrar un tooltip con los
   *   últimos 10 valores.
   * - Se puede hacer click para seleccionarlo y así mostrar un gráfico con
   *   todos sus valores a lo largo del tiempo.
   *
   * @param {Historial} historial Un historial de datos.
   * @returns {JSX.Element | null} El componente descrito, o null si no hay
   * una descripción asociada.
   */
  const historialAComponente = (historial: Historial): JSX.Element | null => {
    const { tipo_evento, mapa_valores, tiempos, gravedades } = historial;
    const { descripcion, tipo_dato, unidad_medida } = tipo_evento;
    if (descripcion === null) {
      return null;
    }

    let valores = mapa_valores.main;
    if (tipo_dato === "float") {
      valores = valores.map((valor: number) => valor.toFixed(1));
    } else if (tipo_dato === "int") {
      valores = valores.map((valor: number) => valor.toFixed(0));
    } else if (tipo_dato === "bool") {
      valores = valores.map((valor: boolean) => yesNo(valor));
    }
    const maxLength = Math.max(
      0,
      ...valores.map((valor: string) => valor.length),
    );
    if (["float", "int"].includes(tipo_dato)) {
      valores = valores.map((valor: string) => valor.padStart(maxLength));
    }

    let stds: string[] | null = null;
    if (mapa_valores.desvest !== undefined) {
      stds = mapa_valores.desvest.map((std: number) => std.toFixed(1));
      const stdMaxLength = Math.max(
        0,
        ...stds.map((std: string) => std.length),
      );
      stds = stds.map((std: string) => std.padStart(stdMaxLength));
    }

    // Valores por defecto cuando no hay valores.
    let tooltip = <p>No hay datos.</p>;
    let ultimoValor: any = "---";
    let textColor: string | undefined = "hsl(0, 0%, 75%)";

    if (valores.length > 0) {
      const tooltipList = [];
      let finalIndex = valores.length;

      // Si son demasiados valores, se muestran solo los últimos 10.
      if (valores.length > 11) {
        finalIndex = 10;
      }

      // Se muestra valores del más reciente al más antiguo, de arriba hacia abajo.
      for (let i = 0; i < finalIndex; i++) {
        const tooltipLineTextColor = getLightTextIssueColor(gravedades[i]);
        let valor = valores[i];
        let textLine = `${parseTime(tiempos[i])} ║ ${valor}`;
        if (stds !== null) {
          textLine += ` ± ${stds[i]}`;
        }
        if (unidad_medida !== null) {
          textLine += unidad_medida;
        }
        tooltipList.push(
          <pre key={i} style={{ color: tooltipLineTextColor }}>{textLine}</pre>,
        );
      }

      // Si son demasiados valores, se muestran solo los últimos 10.
      if (finalIndex < valores.length) {
        tooltipList.push(
          <pre key="end" style={{ color: "hsl(0, 0%, 60%)" }}>
            ({valores.length - finalIndex} valores más)
          </pre>,
        );
      }

      // Se actualiza las variables anteriores que tenían valores por defecto.
      tooltip = (
        <div style={{ fontFamily: "Monospace", lineHeight: "1.25em" }}>
          {tooltipList}
        </div>
      );
      ultimoValor = valores[0];
      if (stds !== null) {
        ultimoValor += ` ± ${stds[0]}`;
      }
      if (unidad_medida !== null) {
        ultimoValor += unidad_medida;
      }
      textColor = getDarkTextIssueColor(gravedades[0]);
    }

    return (
      <Tooltip key={historial.tipo_evento.descripcion} hasArrow label={tooltip}>
        <p
          style={{ color: textColor, cursor: "pointer" }}
          onClick={() => setHistorial(historial)}
        >
          <strong>{descripcion}:</strong> {ultimoValor}
        </p>
      </Tooltip>
    );
  };

  const breaks = [];
  for (let i = (eventos.length - 1) % 20; i < 20 - 1 - 2; i++) {
    breaks.push(<p key={i}></p>);
  }

  // Calcular número de filas en el dashboard.
  const numRowsInColumn1 = Math.ceil(eventos.length / 2);
  const numRowsInColumn3 = mediciones_regulares.length + lotes.length + 2 + 2 + 2;
  const numRows = Math.max(numRowsInColumn1, numRowsInColumn3);
  const gridTemplateRows = `repeat(${numRows}, auto)`

  return (
    <Box px={6} py={{ base: 3, lg: 5 }}>
      <HStack justifyContent="space-between">
        <Button
          onClick={() => navigate("/")}
          borderRadius="lg"
          aria-label="Regresar"
        >
          <ArrowBackIcon />
          &ensp;Regresar
        </Button>
        <Heading textAlign="center" as="h1">
          FirmPro {nfp}: {getName()}
        </Heading>
        <HStack gap={4}>
          {
          /* Links a páginas relacionadas */
          [
            ["FruitCloud", "https://fruit.happyvolt.com/privado/informes"],
            ["SGFP", "https://gestion.happyvolt.com/private/firmpro"],
          ].map(list => {
            const [pageName, link] = list;
            return <a href={link} style={{ color: "blue", textDecorationLine: "underline" }}>
              {pageName}
            </a>;
          })}


          {/* Modal de galería de fotos */}
          <Button onClick={onOpenGallery} borderRadius="lg" aria-label="Galería">
            Galería&ensp;
            <MdPhoto />
          </Button>
          <CustomLightbox
            open={isGalleryOpen}
            close={() => setIsGalleryOpen(false)}
            slides={photoSlides}
          />

          {/* Modal de configuración del equipo */}
          <Button onClick={onFPSettingsModalOpen} borderRadius="lg" aria-label="Configurar equipo">
            Configurar equipo&ensp;
            <SettingsIcon />
          </Button>
          <Modal
            size="xl"
            scrollBehavior="inside"
            isOpen={isFPSettingsModalOpen}
            onClose={onFPSettingsModalClose}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                Configurar equipo {nfp}: {getName()}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <FPConfigForm nfp={nfp} />
              </ModalBody>
            </ModalContent>
          </Modal>
        </HStack>
        
      </HStack>

      <HStack>
        <div
          style={{
            display: "grid",
            gridTemplateRows: gridTemplateRows,
            gridAutoFlow: "column",
            paddingBlock: "1rem",
            columnGap: "1.3rem",
            lineHeight: "1.5em",
          }}
        >
          {eventos.map(historialAComponente)}
          {eventos.length % 2 === 1 ? <br /> : null}
          {historialAComponente(historial_conexion)}
          {historialAComponente(ip)}
          <br />

          <Heading as="h3" fontSize="xl" textAlign="center">
            Mediciones regulares
          </Heading>
          {mediciones_regulares.map(historialAComponente)}
          <br />

          <Heading as="h3" fontSize="xl" textAlign="center">
            Lotes
          </Heading>
          {lotes.map(historialAComponente)}
        </div>

        {/* Gráficos */}
        <FPPlot historial={historial} />
      </HStack>

      {/* Errores */}
      <HStack gap="4rem" justifyContent="center">
        <IssueLog fallasPorDia={fallas_por_dia} />
        <Button colorScheme="pink" onClick={() => setHistorial(fallas)}>
          Mostrar todas las fallas
        </Button>
      </HStack>
    </Box>
  );
}

function yesNo(value: boolean): string {
  return value ? "Sí" : "No";
}
